// The IDENTITY LINK TOKEN (#7426 decision 2) — the single-use, 30-minute link we
// mail to one address so the human signed in as user A can prove they also own
// user B (same email) and bind the two.
//
//   https://api.developerz.ai/auth/link/confirm/<jwt>
//
// WHAT IT BINDS, and why each claim is there:
//   - `sub` + `sid` — the requesting user AND the session that asked. The confirm
//     leg refuses unless THAT session, still live, opens the link: a link forwarded
//     to someone else (or opened in another browser) authorizes nothing.
//   - `tgt` — the other user. Never client-supplied at confirm time.
//   - `email` — the address the link was mailed to; the confirm re-checks it still
//     belongs to both users.
//   - `jti` — the single-use key (`single_use_claims`, namespaced by the api).
//   - `aud` — a fixed purpose, so a token minted under the same secret for any
//     other purpose can never verify here.
//
// The payload is base64url, NOT encryption: it carries ids and the one address the
// mail already went to, nothing more. Shape, signature, payload, expiry — in that
// order — exactly as `./blocker-fix-link.ts` verifies.
//
// Namespace import: see the note in `./jws` (browser-evaluability, #180).
import * as nodeCrypto from 'node:crypto';
import { z } from 'zod';
import { encodeHeader, encodeJson, openCompactJws, signSegments } from './jws';

/** Thirty minutes: long enough to open a mailbox, short enough to be dead in an archive. */
export const IDENTITY_LINK_TTL_SECONDS = 30 * 60;

/** The fixed purpose claim. */
const IDENTITY_LINK_AUD = 'identity_link' as const;

/** The path the confirm leg is served from. One place, so the minter and the route agree. */
export const IDENTITY_LINK_CONFIRM_PATH = '/auth/link/confirm' as const;

export const IdentityLinkTokenPayloadSchema = z.strictObject({
  aud: z.literal(IDENTITY_LINK_AUD),
  /** The requesting user — the survivor of the merge. */
  sub: z.uuid(),
  /** The requesting session (`sessions.id`). */
  sid: z.uuid(),
  /** The user to absorb. */
  tgt: z.uuid(),
  /** The address the link was mailed to (lowercased). */
  email: z.email(),
  iat: z.number().int().nonnegative(),
  exp: z.number().int().positive(),
  jti: z.string().min(8).max(128),
});
export type IdentityLinkTokenPayload = z.infer<typeof IdentityLinkTokenPayloadSchema>;

export type SignIdentityLinkInput = {
  requesterUserId: string;
  sessionId: string;
  targetUserId: string;
  email: string;
  /** Test seam — defaults to `Date.now`. */
  now?: () => number;
  /** Test seam — defaults to `crypto.randomUUID`. */
  newJti?: () => string;
};

/** Mint the compact JWS. THROWS on an empty secret or anything the schema refuses. */
export function signIdentityLinkToken(
  input: SignIdentityLinkInput,
  secret: string,
): { token: string; payload: IdentityLinkTokenPayload } {
  if (!secret) throw new Error('[@developerz/domain] signIdentityLinkToken: secret is empty');
  const now = input.now ?? Date.now;
  const iat = Math.floor(now() / 1000);
  const payload = IdentityLinkTokenPayloadSchema.parse({
    aud: IDENTITY_LINK_AUD,
    sub: input.requesterUserId,
    sid: input.sessionId,
    tgt: input.targetUserId,
    email: input.email.toLowerCase(),
    iat,
    exp: iat + IDENTITY_LINK_TTL_SECONDS,
    jti: (input.newJti ?? (() => nodeCrypto.randomUUID()))(),
  } satisfies Record<string, unknown>);
  const headerB64 = encodeHeader();
  const payloadB64 = encodeJson(payload);
  return {
    token: `${headerB64}.${payloadB64}.${signSegments(headerB64, payloadB64, secret)}`,
    payload,
  };
}

export type IdentityLinkTokenFailure = 'malformed' | 'bad-signature' | 'bad-payload' | 'expired';

/** Verify a link. Tagged result, never a throw on bad input. */
export function verifyIdentityLinkToken(
  token: string,
  secret: string,
  opts: { now?: () => number } = {},
):
  | { ok: true; payload: IdentityLinkTokenPayload }
  | { ok: false; reason: IdentityLinkTokenFailure } {
  if (!secret) throw new Error('[@developerz/domain] verifyIdentityLinkToken: secret is empty');
  const opened = openCompactJws(token, secret);
  if (!opened.ok) return { ok: false, reason: opened.reason };
  const parsed = IdentityLinkTokenPayloadSchema.safeParse(opened.payload);
  if (!parsed.success) return { ok: false, reason: 'bad-payload' };
  if (parsed.data.exp * 1000 <= (opts.now ?? Date.now)()) return { ok: false, reason: 'expired' };
  return { ok: true, payload: parsed.data };
}

/** The absolute URL a human clicks. The token rides a PATH segment (`./blocker-fix-link.ts` `blockerFixUrl`). */
export function identityLinkConfirmUrl(apiBaseUrl: string, token: string): string {
  return `${apiBaseUrl.replace(/\/+$/, '')}${IDENTITY_LINK_CONFIRM_PATH}/${encodeURIComponent(token)}`;
}
